import React, { useEffect, useState } from 'react'
import { Keyboard, Platform, StyleSheet, TouchableOpacity, View } from 'react-native'
import { BottomTabBarProps } from '@react-navigation/bottom-tabs'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, NotifBadge } from '@components'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useUnreadNotificationsCount } from '@hooks'

export const TabBar = ({ state, descriptors, navigation }: BottomTabBarProps) => {
  const theme = useTheme()
  const { unreadCount } = useUnreadNotificationsCount()

  const { bottom } = useSafeAreaInsets()

  const styles = getStyle(theme, bottom)

  // `tabBarHideOnKeyboard` is only honoured by react-navigation's default
  // BottomTabBar — with this custom `tabBar` we have to reimplement it.
  // Android only: there `adjustResize` shrinks the window on keyboard open,
  // so the tab bar would otherwise sit *between* the input and the keyboard.
  // On iOS the keyboard overlays the tab bar (no resize), so it never shows
  // a gap and we leave that path untouched.
  const [keyboardShown, setKeyboardShown] = useState(false)
  useEffect(() => {
    if (Platform.OS !== 'android') return
    const show = Keyboard.addListener('keyboardDidShow', () => setKeyboardShown(true))
    const hide = Keyboard.addListener('keyboardDidHide', () => setKeyboardShown(false))
    return () => {
      show.remove()
      hide.remove()
    }
  }, [])

  const focusedRoute = state.routes[state.index]
  const hideOnKeyboard = descriptors[focusedRoute.key]?.options.tabBarHideOnKeyboard
  if (keyboardShown && hideOnKeyboard) return null

  return (
    Platform.OS !== 'web' && (
      <View style={styles.tabBarContainer}>
        {state.routes.map((route, index) => {
          const { options } = descriptors[route.key]
          const { tabBarIcon } = options

          const isFocused = state.index === index

          const onPress = () => {
            const event = navigation.emit({
              type: 'tabPress',
              target: route.key,
              canPreventDefault: true,
            })

            if (!isFocused && !event.defaultPrevented) {
              // The `merge: true` option makes sure that the params inside the tab screen are preserved
              navigation.navigate(route.name, { merge: true })
            }
          }

          const tabBarIconProps = {
            focused: isFocused,
            size: theme.fontSize.large,
          }

          return (
            <TouchableOpacity
              key={index}
              accessibilityRole="button"
              accessibilityState={isFocused ? { selected: true } : {}}
              accessibilityLabel={options.tabBarAccessibilityLabel}
              // Deterministic testID for e2e flows (Maestro / Playwright). Falls back
              // to `options.tabBarTestID` when set so callers can override.
              testID={options.tabBarTestID ?? `tab-${route.name}`}
              onPress={onPress}
              style={[styles.tabBarItem]}
            >
              <View style={styles.iconWrapper}>
                {tabBarIcon?.({
                  ...tabBarIconProps,
                  color: isFocused ? theme.colors.primary : theme.colors.secondary,
                })}
                {route.name === 'Notifications' && (
                  <NotifBadge total={unreadCount} testID="tab-notifications-badge" />
                )}
              </View>
              <Text
                type="bodyExtraSmall"
                style={{ paddingTop: theme.spacing.xs }}
                color={isFocused ? theme.colors.primary : theme.colors.secondary}
              >
                {options.title}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    )
  )
}

const getStyle = (theme: Theme, bottomInset: number) =>
  StyleSheet.create({
    tabBarContainer: {
      flexDirection: 'row',
      shadowColor: 'black',
      shadowOffset: {
        width: 0,
        height: -3,
      },
      shadowOpacity: 0.22,
      shadowRadius: 3.84,
      elevation: 5,
      backgroundColor: 'white',
    },
    tabBarItem: {
      flex: 1,
      paddingBottom: bottomInset || theme.spacing.md,
      paddingTop: theme.spacing.md,
      alignItems: 'center',
    },
    iconWrapper: {
      position: 'relative',
    },
  })
